import { LegalLayout, CLINIC } from "../components/LegalLayout";

export default function InformedConsent() {
  return (
    <LegalLayout title="Consentimiento Informado — Telemedicina, Receta Electrónica e IA">
      <p className="lead text-base">
        Conforme a la <b>Ley 41/2002</b>, de 14 de noviembre, básica reguladora de la autonomía
        del paciente, al <b>Real Decreto 1718/2010</b> sobre receta médica y al{" "}
        <b>Reglamento (UE) 2024/1689 (AI Act)</b>, antes de utilizar los servicios de {CLINIC.name}
        usted debe ser informado y prestar su consentimiento expreso a los tres apartados
        siguientes.
      </p>

      <h2>1. Médico responsable</h2>
      <p>Dr. Dimy Laguerre, médico generalista colegiado nº {CLINIC.colegiado} en el Ilustre Colegio
      Oficial de Médicos de Sevilla. Contacto: <a href={`mailto:${CLINIC.email}`}>{CLINIC.email}</a>.
      Dirección de la consulta: {CLINIC.address}.</p>

      {/* ============ BLOQUE A: TELEMEDICINA ============ */}
      <div className="not-prose my-6 rounded-xl border-2 border-primary/30 bg-primary/5 p-1">
        <div className="bg-primary text-primary-foreground rounded-t-lg px-4 py-2 font-semibold">
          A · Consentimiento para Telemedicina
        </div>
        <div className="p-5 prose prose-sm max-w-none">

          <h3 className="!mt-0">A.1 Descripción del servicio</h3>
          <p>La telemedicina consiste en la prestación de servicios médicos a distancia mediante
          tecnologías de videoconsulta, mensajería médica y plataforma electrónica. Incluye:</p>
          <ul>
            <li>Anamnesis (entrevista clínica) por videoconferencia (Jitsi Meet, cifrado extremo a extremo).</li>
            <li>Exploración visual limitada según permita la modalidad telemática.</li>
            <li>Orientación diagnóstica y terapéutica.</li>
            <li>Emisión de recetas y peticiones de pruebas cuando proceda (véase bloque B).</li>
            <li>Seguimiento clínico no presencial.</li>
          </ul>

          <h3>A.2 Beneficios esperados</h3>
          <ul>
            <li>Acceso a atención médica sin desplazamientos.</li>
            <li>Reducción de tiempos de espera.</li>
            <li>Continuidad asistencial y multilingüe (ES / FR / HT).</li>
          </ul>

          <h3>A.3 Limitaciones y riesgos</h3>
          <ul>
            <li><b>Exploración física limitada</b>: no es posible palpación, auscultación
            directa, ni mediciones objetivas (tensión, temperatura) salvo con dispositivos propios del paciente.</li>
            <li><b>Calidad técnica</b>: depende de la conexión a internet de ambas partes.</li>
            <li><b>Confidencialidad</b>: la plataforma usa cifrado TLS 1.3 y almacenamiento
            cifrado (AES-256 en reposo con Fernet a nivel de campo para datos especialmente
            sensibles), pero ningún sistema electrónico está libre de riesgos al 100%.</li>
            <li><b>Casos no aptos</b>: patologías o urgencias que requieren atención presencial;
            el profesional le derivará al centro adecuado.</li>
            <li><b>Urgencias vitales</b>: llame al <b>112</b>. La telemedicina NO sustituye a emergencias.</li>
          </ul>

          <h3>A.4 Grabación</h3>
          <p>La videoconsulta <b>NO se graba</b> salvo solicitud expresa por escrito y consentimiento
          adicional específico. Las notas clínicas y documentos generados quedan registrados en su
          historia clínica electrónica conforme a la Ley 41/2002.</p>
        </div>
      </div>

      {/* ============ BLOQUE B: RECETA ELECTRÓNICA ============ */}
      <div className="not-prose my-6 rounded-xl border-2 border-emerald-300/60 bg-emerald-50/40 p-1">
        <div className="bg-emerald-700 text-white rounded-t-lg px-4 py-2 font-semibold">
          B · Consentimiento para Receta Electrónica (compatible REMPE)
        </div>
        <div className="p-5 prose prose-sm max-w-none">

          <h3 className="!mt-0">B.1 Marco legal</h3>
          <p>Real Decreto 1718/2010, sobre receta médica y órdenes de dispensación. La receta
          electrónica privada emitida por esta plataforma es <b>compatible REMPE</b> (Receta
          Médica Privada Electrónica del Consejo General de Colegios Oficiales de Médicos).</p>

          <h3>B.2 Cómo funciona</h3>
          <ul>
            <li>Al finalizar la consulta, el médico emite la receta con sus datos, principio activo,
            posología, duración y advertencias.</li>
            <li>Se genera un <b>PDF firmado</b> con datos del prescriptor (colegiado
            nº {CLINIC.colegiado}, especialidad, dirección) y un <b>código QR único</b>.</li>
            <li>El PDF se envía por email al paciente y queda archivado en su historia clínica.</li>
            <li>La farmacia puede <b>verificar la autenticidad</b> escaneando el QR o accediendo a
            <code> /verify/{"{código}"}</code> en drdimylaguerre.com.</li>
          </ul>

          <h3>B.3 Validez y limitaciones</h3>
          <ul>
            <li><b>Validez legal</b>: la receta privada es válida en toda España para dispensación
            en farmacia privada.</li>
            <li><b>Estupefacientes y psicótropos</b>: sujetos a talonarios oficiales del Colegio,
            no emitidos por esta plataforma hasta la integración con REMPE oficial.</li>
            <li><b>Financiación pública</b>: esta receta es privada y NO da derecho a
            financiación por el Sistema Nacional de Salud.</li>
            <li><b>Duración</b>: 10 días desde su emisión para primera dispensación, salvo indicación específica.</li>
          </ul>

          <h3>B.4 Custodia y trazabilidad</h3>
          <p>Toda receta queda registrada con: fecha, hora, IP del profesional, hash inmutable del
          contenido y firma del prescriptor. Se conserva un mínimo de <b>5 años</b> conforme a la
          Ley 41/2002 (Art. 17).</p>
        </div>
      </div>

      {/* ============ BLOQUE C: INTELIGENCIA ARTIFICIAL ============ */}
      <div className="not-prose my-6 rounded-xl border-2 border-amber-300 bg-amber-50/40 p-1">
        <div className="bg-amber-600 text-white rounded-t-lg px-4 py-2 font-semibold">
          C · Consentimiento para el uso de Inteligencia Artificial
        </div>
        <div className="p-5 prose prose-sm max-w-none">

          <h3 className="!mt-0">C.1 Alcance</h3>
          <p>Esta plataforma utiliza IA generativa (modelo <b>Claude Sonnet 4.5</b> de Anthropic)
          en dos contextos: chat público de orientación y asistente clínico interno para el
          doctor. Consulta el detalle completo en la <a href="/uso-ia">Política de uso de IA</a>.</p>

          <h3>C.2 Finalidad y limitaciones</h3>
          <ul>
            <li>La IA <b>orienta</b>; NO diagnostica, NO prescribe y NO sustituye a un médico.</li>
            <li>Toda decisión clínica corresponde al <b>Dr. Dimy Laguerre</b> con supervisión
            humana explícita (AI Act Art. 14).</li>
            <li>El chat público es anónimo si no ha iniciado sesión.</li>
            <li>Los datos del prompt <b>NO se usan para entrenar modelos</b> según contrato
            RGPD (Art. 28) con el proveedor.</li>
          </ul>

          <h3>C.3 Transparencia y derechos</h3>
          <ul>
            <li>Cada respuesta de la IA se etiqueta visualmente como generada por IA (AI Act Art. 52).</li>
            <li>Al iniciar el chat verá un modal con este disclaimer que debe aceptar.</li>
            <li>Puede retirar el consentimiento cerrando el chat y borrando la preferencia
            <code> ai_chat_consent</code> en la <a href="/politica-cookies">política de cookies</a>.</li>
          </ul>

          <h3>C.4 Responsabilidad</h3>
          <p>El paciente comprende que la orientación de la IA es informativa y <b>NO constituye
          asesoramiento médico</b>. Ante síntomas graves o dudas, debe contactar directamente con
          el Dr. Dimy Laguerre o con los servicios de emergencia.</p>
        </div>
      </div>

      <h2>2. Tratamiento de datos personales</h2>
      <p>Sus datos serán tratados conforme a nuestra <a href="/privacidad">Política de Privacidad</a>,
      al <b>RGPD</b> (Reg. UE 2016/679), a la <b>LOPDGDD</b> (Ley Orgánica 3/2018) y a la{" "}
      <b>Ley 41/2002</b>. La categoría especial «datos de salud» (Art. 9.2.h RGPD) se protege con
      encriptación en reposo (Fernet a nivel de campo), en tránsito (TLS 1.3) y auditoría inmutable
      de accesos.</p>

      <h2>3. Derechos del paciente (Ley 41/2002 + RGPD)</h2>
      <ul>
        <li>Recibir información comprensible sobre su proceso asistencial.</li>
        <li>Decidir libremente entre las opciones clínicas disponibles.</li>
        <li>Negarse al tratamiento, excepto en los casos previstos por la ley.</li>
        <li>Acceder a su historia clínica y obtener copia (Art. 15 RGPD — <a href="/app/profile">disponible en tu perfil</a>).</li>
        <li>Portabilidad de sus datos en JSON estructurado (Art. 20 RGPD).</li>
        <li>Solicitar una segunda opinión médica.</li>
        <li>Revocar este consentimiento en cualquier momento sin perjuicio de la asistencia ya prestada.</li>
        <li>Presentar reclamación ante la <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer">Agencia Española de Protección de Datos</a>.</li>
      </ul>

      <h2>4. Confidencialidad y secreto profesional</h2>
      <p>Toda la información derivada de la consulta queda amparada por el secreto profesional
      médico (Art. 199 Código Penal, Art. 10 Ley 41/2002 y Código de Deontología Médica).</p>

      <h2>5. Declaración de consentimiento</h2>
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5 not-prose">
        <p className="font-medium text-emerald-900 mb-3">
          Al registrarme y utilizar {CLINIC.name} declaro que:
        </p>
        <ul className="space-y-2 text-sm text-emerald-900">
          <li>✓ He leído y comprendido los bloques A (Telemedicina), B (Receta Electrónica) y C (Uso de IA).</li>
          <li>✓ He tenido la oportunidad de plantear preguntas y aclarar dudas.</li>
          <li>✓ Entiendo las limitaciones inherentes a la telemedicina y a la IA.</li>
          <li>✓ Reconozco que la receta electrónica emitida es privada y compatible REMPE.</li>
          <li>✓ Acepto el tratamiento de mis datos personales y de salud conforme a la Política de Privacidad.</li>
          <li>✓ Soy consciente de que puedo revocar este consentimiento en cualquier momento.</li>
          <li>✓ Doy mi consentimiento libre, expreso e informado para los servicios descritos.</li>
        </ul>
      </div>

      <p className="mt-6 text-xs text-muted-foreground">
        Este consentimiento queda registrado electrónicamente con marca de tiempo, dirección IP y
        versión del documento (versión actual: <b>2026.02</b>) en un ledger inmutable
        (<code>db.consents_history</code>) al momento del registro. Conforme al Art. 7.1 RGPD, la
        plataforma dispone de prueba documental de su consentimiento y puede exhibirla ante la AEPD.
      </p>
    </LegalLayout>
  );
}
